import { IncDataSrcOutline } from '@/common/entities/incDataSrc/IncDataSrcOutline';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { useNavStateSync } from '@/common/hooks/navState/useNavStateSync';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

export const IncDataSrcList = ({ filterId, progProdId }) => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();
  const [progProdIncDataSrc, setProgProdIncDataSrc] = useState({ progProdId: null, incDataSrcIds: [] });
  const { navigateTo } = useNavState();
  const entityListRef = useRef();

  useEffect(() => {
    if (filterId || !progProdId) return;

    let active = true;
    rsrc.rows('md', 'PROG_PROD', { expr: ExprUtils.whereParams('Id', progProdId) }).then((rows) => {
      if (active) {
        setProgProdIncDataSrc({ progProdId, incDataSrcIds: rows.map((row) => row.incDataSrcId) });
      }
    });

    return () => {
      active = false;
    };
  }, [filterId, progProdId, rsrc]);

  const isLoadingProgProdIncDataSrcs = !filterId && progProdId && progProdIncDataSrc.progProdId !== progProdId;

  const [isOutline, setIsOutline] = useNavStateSync('incDataSrcIsOutlineOn', false);
  const isOutlineActive = isOutline && (!!filterId || (!!progProdId && progProdIncDataSrc.incDataSrcIds.length > 0));

  const augRowsReq = useCallback(
    (rowsReq) => {
      if (filterId) {
        rowsReq.expr = ExprUtils.and(rowsReq.expr, ExprUtils.whereParams('filterId', filterId));
      } else {
        rowsReq.expr = ExprUtils.and(rowsReq.expr, ExprUtils.whereParams('id', progProdIncDataSrc.incDataSrcIds));
      }
    },
    [filterId, progProdIncDataSrc.incDataSrcIds],
  );

  const { augFieldDefs, clientSideRowData, getContextMenuItems } = IncDataSrcOutline(
    module,
    { filterId, incDataSrcIds: progProdIncDataSrc.incDataSrcIds },
    isOutlineActive,
    { entityListRef, navigateTo },
  );

  const augTabButtons = (buttons) => {
    if (!filterId && !progProdId) return;

    buttons.push(
      <ToggleButtonGroup
        value={isOutline ? 'outline' : 'flat'}
        exclusive
        onChange={(e, value) => {
          setIsOutline(value === 'outline');
        }}
        sx={{ height: 40, color: 'primary.main' }}
      >
        <ToggleButton value="outline" data-test-id="toggle-outline" sx={{ whiteSpace: 'nowrap' }}>
          Outline
        </ToggleButton>
        <ToggleButton value="flat" data-test-id="toggle-flat" sx={{ whiteSpace: 'nowrap' }}>
          Flat
        </ToggleButton>
      </ToggleButtonGroup>,
    );

    buttons.reverse();
  };

  if (isLoadingProgProdIncDataSrcs) return null;

  return (
    <EntityList
      ref={entityListRef}
      key={isOutlineActive}
      entity="INC_DATA_SRC"
      augRowsReq={augRowsReq}
      augFieldDefs={augFieldDefs}
      clientSideRowData={isOutlineActive ? clientSideRowData : null}
      augTabButtons={augTabButtons}
      gridOptions={{
        treeData: isOutlineActive,
        ...(isOutlineActive ? { getContextMenuItems } : {}),
      }}
    />
  );
};
